import React, { useState, useEffect, useRef } from 'react';
import { db } from './firebase';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  updateDoc,
  writeBatch,
  doc,
  limit,
  deleteDoc,
} from 'firebase/firestore';

export default function NotificationBell({ userId }) {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  const dropdownRef = useRef(null);

  // Real-time listener for this user's notifications (latest 50)
  useEffect(() => {
    if (!userId) return;

    const q = query(
      collection(db, 'notifications'),
      where('userId', '==', userId),
      orderBy('timestamp', 'desc'),
      limit(50)
    );

    const unsubscribe = onSnapshot(
      q, 
      (snapshot) => {
        const notifs = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        setNotifications(notifs);
        setUnreadCount(notifs.filter(n => !n.read).length);
      },
      (error) => {
        console.error('Error fetching notifications:', error);
        // Fallback to empty state on error
        setNotifications([]);
        setUnreadCount(0);
      }
    );

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [userId]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  async function markAsRead(notification) {
    if (notification.read) return;
    await updateDoc(doc(db, 'notifications', notification.id), { read: true });
  }

  async function markAllRead() {
    const unread = notifications.filter((n) => !n.read);
    if (!unread.length) return;
    const batch = writeBatch(db);
    unread.forEach((n) => batch.update(doc(db, 'notifications', n.id), { read: true }));
    await batch.commit();
  }

  async function deleteAll() {
    try {
      const batch = writeBatch(db);
      notifications.forEach((n) => batch.delete(doc(db, 'notifications', n.id)));
      await batch.commit();
    } catch (err) {
      console.error('Error deleting notifications:', err);
    } finally {
      setShowDeleteConfirm(false);
    }
  }

  function formatTime(timestamp) {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  }

  return (
    <div ref={dropdownRef} style={styles.wrapper}>
      {/* Bell button */}
      <button
        style={styles.bellButton}
        onClick={() => setOpen((v) => !v)}
        aria-label="Notifications"
        onMouseEnter={(e) => { e.currentTarget.style.background = '#252525'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
      >
        <span style={styles.bellIcon}>{open ? '🔔' : unreadCount > 0 ? '🔔' : '🔕'}</span>
        {unreadCount > 0 && (
          <span style={styles.badge}>
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown panel */}
      {open && (
        <div style={styles.dropdown}>
          {/* Header */}
          <div style={styles.dropdownHeader}>
            <span style={styles.dropdownTitle}>Notifications</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {unreadCount > 0 && (
                <button
                  style={styles.markAllBtn}
                  onClick={markAllRead}
                  onMouseEnter={(e) => { e.currentTarget.style.color = '#d4af37'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = '#9ca3af'; }}
                >
                  Mark all read
                </button>
              )}
              {notifications.length > 0 && (
                showDeleteConfirm ? (
                  <>
                    <button
                      style={styles.deleteConfirmBtn}
                      onClick={deleteAll}
                      onMouseEnter={(e) => { e.currentTarget.style.background = '#b91c1c'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = '#ef4444'; }}
                    >
                      Delete
                    </button>
                    <button
                      style={styles.markAllBtn}
                      onClick={() => setShowDeleteConfirm(false)}
                      onMouseEnter={(e) => { e.currentTarget.style.color = '#d4af37'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = '#9ca3af'; }}
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <button
                    style={styles.deleteXBtn}
                    onClick={() => setShowDeleteConfirm(true)}
                    onMouseEnter={(e) => { e.currentTarget.style.color = '#ef4444'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = '#6b7280'; }}
                    aria-label="Delete all notifications"
                  >
                    ✕
                  </button>
                )
              )}
            </div>
          </div>

          {/* List */}
          <div style={styles.list}>
            {notifications.length === 0 ? (
              <div style={styles.empty}>No notifications yet</div>
            ) : (
              notifications.map((n) => (
                <div
                  key={n.id}
                  style={{
                    ...styles.item,
                    background: n.read ? 'transparent' : '#1a1a2e',
                    borderLeft: n.read ? '3px solid transparent' : '3px solid #d4af37',
                  }}
                  onClick={() => markAsRead(n)}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#252525'; }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = n.read ? 'transparent' : '#1a1a2e';
                  }}
                >
                  <span style={styles.itemIcon}>{n.icon || '📢'}</span>
                  <div style={styles.itemBody}>
                    <div style={styles.itemTitle}>{n.title}</div>
                    <div style={styles.itemMessage}>{n.message}</div>
                    <div style={styles.itemTime}>{formatTime(n.timestamp)}</div>
                  </div>
                  {!n.read && <span style={styles.unreadDot} />}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  wrapper: {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
  },
  bellButton: {
    position: 'relative',
    background: 'transparent',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    padding: '6px 8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background 0.15s ease',
    outline: 'none',
  },
  bellIcon: {
    fontSize: '20px',
    lineHeight: 1,
    userSelect: 'none',
  },
  badge: {
    position: 'absolute',
    top: '2px',
    right: '2px',
    background: '#ef4444',
    color: '#fff',
    fontSize: '10px',
    fontWeight: '700',
    minWidth: '16px',
    height: '16px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 3px',
    lineHeight: 1,
    pointerEvents: 'none',
  },
  dropdown: {
  position: 'fixed', // CHANGE FROM absolute TO fixed
  top: '60px', // Distance from top of screen
  right: '10px', // Distance from right edge of screen
  width: '340px',
  maxWidth: 'calc(100vw - 20px)',
  background: '#1e1e1e',
  border: '1px solid #2d2d2d',
  borderRadius: '12px',
  boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
  zIndex: 1000,
  overflow: 'hidden',
  animation: 'notifFadeIn 0.15s ease',
},
  dropdownHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 16px 10px',
    borderBottom: '1px solid #2d2d2d',
  },
  dropdownTitle: {
    color: '#ffffff',
    fontWeight: '700',
    fontSize: '15px',
    letterSpacing: '0.02em',
  },
  markAllBtn: {
    background: 'none',
    border: 'none',
    color: '#9ca3af',
    fontSize: '12px',
    cursor: 'pointer',
    padding: '2px 4px',
    transition: 'color 0.15s ease',
    outline: 'none',
  },
  deleteXBtn: {
    background: 'none',
    border: 'none',
    color: '#6b7280',
    fontSize: '14px',
    cursor: 'pointer',
    padding: '2px 4px',
    transition: 'color 0.15s ease',
    outline: 'none',
    lineHeight: 1,
  },
  deleteConfirmBtn: {
    background: '#ef4444',
    border: 'none',
    color: '#fff',
    fontSize: '12px',
    fontWeight: '700',
    cursor: 'pointer',
    padding: '3px 10px',
    borderRadius: '6px',
    transition: 'background 0.15s ease',
    outline: 'none',
  },
  list: {
    maxHeight: '380px',
    overflowY: 'auto',
    overflowX: 'hidden',
  },
  empty: {
    padding: '32px 16px',
    textAlign: 'center',
    color: '#6b7280',
    fontSize: '14px',
  },
  item: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    padding: '12px 14px',
    cursor: 'pointer',
    transition: 'background 0.15s ease',
    borderBottom: '1px solid #2d2d2d',
    position: 'relative',
  },
  itemIcon: {
    fontSize: '20px',
    lineHeight: 1,
    marginTop: '1px',
    flexShrink: 0,
  },
  itemBody: {
    flex: 1,
    minWidth: 0,
  },
  itemTitle: {
    color: '#ffffff',
    fontWeight: '600',
    fontSize: '13px',
    marginBottom: '2px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  itemMessage: {
    color: '#9ca3af',
    fontSize: '12px',
    lineHeight: '1.4',
    marginBottom: '4px',
  },
  itemTime: {
    color: '#6b7280',
    fontSize: '11px',
  },
  unreadDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    background: '#d4af37',
    flexShrink: 0,
    marginTop: '4px',
  },
};